/**
 * Which "What's new" releases the user has seen (MEIS-13668, set-based since
 * MEIS-13752) — the one source of truth the pane's "New" markers, the pane's
 * header-button badge and the launcher's news dot all read, so they change
 * together.
 *
 * The seen release ids are remembered per browser in localStorage under the
 * key the MEIS-13490 prototype already used, as a JSON array. An older value
 * is a single id: that release and every release listed after it (older) count
 * as seen. localStorage written in the same tab does not notify that tab, so
 * subscribers learn about a write through a module-local listener set read via
 * `useSyncExternalStore` (same pattern as `ItemDetailHintBar`). Storage may be
 * blocked: a failed read means "nothing seen", and a failed write still keeps
 * the value in memory for this tab, so the dot and badge clear even though the
 * next visit counts the releases as new again.
 */
import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  useVisibleChangelogReleases,
  type ChangelogOptions,
} from "./agentChangelogReleases.js";

const SEEN_KEY = "msys-agent-changelog-seen";

/** Set by a write in this tab; wins over storage, which may have refused it. `undefined` = no write yet. */
let writtenValue: string | null | undefined;

const listeners = new Set<() => void>();

export function subscribeToSeenRelease(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

/** The raw stored value (a JSON array, or a legacy single id); a string so it is a stable snapshot. */
export function readSeenReleases(): string | null {
  if (writtenValue !== undefined) return writtenValue;
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

function parseSeenSet(raw: string): string[] | null {
  if (!raw.startsWith("[")) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every((id): id is string => typeof id === "string")
    ) {
      return parsed;
    }
  } catch {
    // Not JSON: treated as a legacy id below.
  }
  return null;
}

/**
 * The seen ids among `releaseIds` (newest first) for a raw stored value.
 * Nothing stored, or a legacy id that is no longer listed: nothing is seen.
 */
function seenIds(raw: string | null, releaseIds: readonly string[]): string[] {
  if (!raw) return [];
  const set = parseSeenSet(raw);
  if (set) return releaseIds.filter(id => set.includes(id));
  const legacyIndex = releaseIds.indexOf(raw);
  return legacyIndex === -1 ? [] : releaseIds.slice(legacyIndex);
}

/**
 * Remembers every id in `visibleIds` as seen, on top of the stored set (a
 * legacy single id or unparseable value is not a set and contributes nothing).
 * Nothing is pruned: visibility depends on the UI locale's translations, so an
 * id seen in one language but not visible in another must stay seen. The set
 * stays small (one id per release).
 */
export function markReleasesSeen(visibleIds: readonly string[]): void {
  // Both sources: another tab may have written to storage since this tab's
  // last write, and storage may have refused this tab's earlier writes.
  let fromStorage: string | null = null;
  try {
    fromStorage = window.localStorage.getItem(SEEN_KEY);
  } catch {
    // Storage blocked: only the in-memory value contributes.
  }
  const prev = [fromStorage, writtenValue].flatMap(raw =>
    raw ? (parseSeenSet(raw) ?? []) : []
  );
  const next = JSON.stringify([...new Set([...prev, ...visibleIds])]);
  writtenValue = next;
  try {
    window.localStorage.setItem(SEEN_KEY, next);
  } catch {
    // Storage blocked: the in-memory value above still serves this tab.
  }
  listeners.forEach(listener => listener());
}

/** Test seam: forgets this tab's in-memory value so the next read goes to storage. */
export function resetAgentChangelogSeenForTests(): void {
  writtenValue = undefined;
}

/**
 * The listed releases the user has not seen, whatever their position. Someone
 * who never opened the pane, or whose legacy seen id is no longer listed,
 * sees only the newest one as new, never the whole history.
 */
export function unseenReleaseIds(
  releaseIds: readonly string[],
  raw: string | null
): string[] {
  if (!raw || (!parseSeenSet(raw) && !releaseIds.includes(raw))) {
    return releaseIds.slice(0, 1);
  }
  const seen = seenIds(raw, releaseIds);
  return releaseIds.filter(id => !seen.includes(id));
}

/**
 * The unseen releases among the *visible* ones (see
 * `useVisibleChangelogReleases`), whether there are any, and a way to mark the
 * given releases as seen. On a query error or refusal nothing is visible, so there
 * is no unseen release. `{ enabled: false }` (no assistant access) skips the
 * query, so `hasUnseen` is false.
 */
export function useAgentChangelogUnseen(options: ChangelogOptions = {}) {
  const releases = useVisibleChangelogReleases(options);
  const seen = useSyncExternalStore(
    subscribeToSeenRelease,
    readSeenReleases,
    () => null
  );
  const unseenIds = useMemo(
    () =>
      unseenReleaseIds(
        (releases ?? []).map(release => release.id),
        seen
      ),
    [releases, seen]
  );
  const markSeen = useCallback(
    (visibleIds: readonly string[]) => markReleasesSeen(visibleIds),
    []
  );
  return { unseenIds, hasUnseen: unseenIds.length > 0, markSeen };
}
